import { Heading, Text, Button, Card, TextField, IconButton } from 'frosted-ui';
import { Plus16, Trash16 } from '@frosted-ui/icons';
import { newPartnerDraft, type PartnerDraft } from './wizardTypes';

interface StepPlacePartnersProps {
  partners: PartnerDraft[];
  onChange: (partners: PartnerDraft[]) => void;
  /** Usernames Whop says don't exist, by draft key. */
  unknownUsernames?: Record<string, string>;
}

// Mirrors the backend's PLACEMENT_AFFILIATE_PATTERN, so nothing fails only after
// the draft exists. People paste "@name"; the "@" isn't part of the username.
const USERNAME = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,39}$/;

export function cleanUsername(text: string): string {
  return text.trim().replace(/^@/, '');
}

export function isValidUsername(text: string): boolean {
  const cleaned = cleanUsername(text);
  return cleaned === '' || USERNAME.test(cleaned);
}

export const USERNAME_RULE = 'A Whop username: up to 40 letters, numbers, dots, dashes or underscores.';

export default function StepPlacePartners({ partners, onChange, unknownUsernames = {} }: StepPlacePartnersProps) {
  function update(key: string, updates: Partial<PartnerDraft>) {
    onChange(partners.map((p) => (p.key === key ? { ...p, ...updates } : p)));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div>
        <Heading size="5" style={{ marginBottom: 'var(--space-2)' }}>
          Who sends you buyers?
        </Heading>
        <Text size="2" color="gray">
          One link per affiliate, clipper or ad campaign. Give each partner their own link instead
          of your usual one. They all feed one test, and results show each source.
        </Text>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {partners.map((p, index) => {
          const usernameOk = isValidUsername(p.affiliateCode);
          const unknown =
            cleanUsername(p.affiliateCode) !== '' &&
            unknownUsernames[p.key] === cleanUsername(p.affiliateCode);
          return (
            <Card key={p.key} size="2">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                <Text size="2" weight="medium">
                  Link {index + 1}
                </Text>
                {partners.length > 1 && (
                  <IconButton
                    type="button"
                    variant="ghost"
                    color="gray"
                    size="1"
                    aria-label={`Remove link ${index + 1}`}
                    onClick={() => onChange(partners.filter((x) => x.key !== p.key))}
                  >
                    <Trash16 />
                  </IconButton>
                )}
              </div>
              <div className="variant-fields-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <div>
                  <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                    Partner or campaign
                  </Text>
                  <TextField.Root size="2" variant="surface">
                    <TextField.Input
                      placeholder="e.g. Mike (clipper), TikTok ads"
                      maxLength={80}
                      value={p.label}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        update(p.key, { label: e.target.value })
                      }
                    />
                  </TextField.Root>
                </div>
                <div>
                  <Text as="label" size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                    Their Whop username (optional)
                  </Text>
                  <TextField.Root size="2" variant="surface" color={usernameOk && !unknown ? undefined : 'red'}>
                    <TextField.Input
                      placeholder="e.g. mike"
                      maxLength={41}
                      value={p.affiliateCode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        update(p.key, { affiliateCode: e.target.value })
                      }
                    />
                  </TextField.Root>
                  {!usernameOk && (
                    <Text size="1" color="red" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                      {USERNAME_RULE}
                    </Text>
                  )}
                  {unknown && (
                    <Text size="1" color="red" style={{ display: 'block', marginTop: 'var(--space-1)' }}>
                      Whop has no user called “{cleanUsername(p.affiliateCode)}”. Check the spelling, or
                      clear it: without a username the partner gets no credit for sales.
                    </Text>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div>
        <Button
          type="button"
          variant="ghost"
          size="2"
          onClick={() => onChange([...partners, newPartnerDraft()])}
          disabled={partners.length >= 20}
        >
          <Plus16 />
          Add another link
        </Button>
      </div>

      <Text size="1" color="gray">
        Add a username so Whop credits the partner for each sale, at the commission set under
        Affiliates in your Whop dashboard. UTM tags you add to a link carry through to checkout.
      </Text>
    </div>
  );
}
